import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
    CheckCircle2, Circle, Clock, Pencil, X, Save, Plus, ArrowUp, ArrowDown, Trash2, Loader2
} from "lucide-react";

const STATUS_OPTIONS = [
    { value: "Not started", label: "Not started" },
    { value: "In progress", label: "In progress" },
    { value: "On hold", label: "On hold" },
    { value: "Completed", label: "Completed" },
];

const PHASE_STATUS = [
    { value: "complete", label: "Complete", icon: CheckCircle2, cls: "text-emerald-500" },
    { value: "in_progress", label: "In progress", icon: Clock, cls: "text-[#C55A46]" },
    { value: "upcoming", label: "Upcoming", icon: Circle, cls: "text-slate-300" },
];

const PHASE_ICON = Object.fromEntries(PHASE_STATUS.map((p) => [p.value, p]));

export default function ProgressEditor({ clientId, portal, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [percent, setPercent] = useState(portal?.progress?.percent ?? 0);
    const [status, setStatus] = useState(portal?.overview?.status ?? "In progress");
    const [milestone, setMilestone] = useState(portal?.overview?.next_milestone ?? "");
    const [phases, setPhases] = useState(portal?.progress?.phases ?? []);

    // Sync when portal prop changes and we're not editing
    useEffect(() => {
        if (editing) return;
        setPercent(portal?.progress?.percent ?? 0);
        setStatus(portal?.overview?.status ?? "In progress");
        setMilestone(portal?.overview?.next_milestone ?? "");
        setPhases(portal?.progress?.phases ?? []);
    }, [portal, editing]);

    const clampPercent = (v) => {
        const n = parseInt(v, 10);
        if (isNaN(n)) return 0;
        return Math.max(0, Math.min(100, n));
    };

    const startEditing = () => {
        setPercent(portal?.progress?.percent ?? 0);
        setStatus(portal?.overview?.status ?? "In progress");
        setMilestone(portal?.overview?.next_milestone ?? "");
        setPhases(portal?.progress?.phases ?? []);
        setEditing(true);
    };

    const movePhase = (i, delta) => {
        const next = phases.slice();
        const j = i + delta;
        if (j < 0 || j >= next.length) return;
        [next[i], next[j]] = [next[j], next[i]];
        setPhases(next);
    };
    const removePhase = (i) => setPhases(phases.filter((_, idx) => idx !== i));
    const addPhase = () => setPhases([...phases, { name: "New phase", status: "upcoming" }]);
    const updatePhase = (i, patch) => setPhases(phases.map((p, idx) => idx === i ? { ...p, ...patch } : p));

    const cancel = () => {
        setEditing(false);
        setPercent(portal?.progress?.percent ?? 0);
        setStatus(portal?.overview?.status ?? "In progress");
        setMilestone(portal?.overview?.next_milestone ?? "");
        setPhases(portal?.progress?.phases ?? []);
    };

    const save = async () => {
        // Validate
        const clean = phases
            .map((p) => ({ name: (p.name || "").trim(), status: p.status || "upcoming" }))
            .filter((p) => p.name.length > 0);
        const p = clampPercent(percent);
        setSaving(true);
        try {
            await api.patch(`/clients/${clientId}/progress`, {
                percent: p,
                overview_status: status,
                next_milestone: milestone,
                phases: clean,
            });
            toast.success("Progress updated");
            setEditing(false);
            onSaved?.();
        } catch (err) {
            toast.error("Could not save progress", { description: formatApiErrorDetail(err.response?.data?.detail) });
        } finally { setSaving(false); }
    };

    return (
        <div className="rounded-xl border border-slate-200 bg-white p-8" data-testid="progress-editor">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <p className="overline mb-1">Project progress</p>
                    {editing ? (
                        <div className="flex items-center gap-2 mt-1">
                            <Input type="number" min={0} max={100}
                                   value={percent}
                                   onChange={(e) => setPercent(clampPercent(e.target.value))}
                                   onBlur={(e) => setPercent(clampPercent(e.target.value))}
                                   data-testid="progress-percent-input"
                                   className="w-24 ring-terra" />
                            <span className="font-display text-2xl tracking-tight text-slate-500">% complete</span>
                        </div>
                    ) : (
                        <h3 className="font-display text-2xl tracking-tight">{portal?.progress?.percent ?? 0}% complete</h3>
                    )}
                </div>
                {editing ? (
                    <div className="flex items-center gap-2">
                        <button onClick={cancel} data-testid="progress-cancel-btn"
                                className="text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1.5">
                            <X className="w-3.5 h-3.5" /> Cancel
                        </button>
                        <button onClick={save} disabled={saving} data-testid="progress-save-btn"
                                className="btn-terra text-sm font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-70">
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            Save
                        </button>
                    </div>
                ) : (
                    <button onClick={startEditing} data-testid="progress-edit-btn"
                            className="text-sm font-medium text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 transition-colors">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                )}
            </div>

            <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-8">
                <div className="h-full bg-[#C55A46] transition-[width] duration-500"
                     style={{ width: `${editing ? clampPercent(percent) : (portal?.progress?.percent ?? 0)}%` }} />
            </div>

            {editing && (
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div className="space-y-2">
                        <Label className="text-slate-700">Project status</Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger data-testid="progress-status-select" className="ring-terra">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {STATUS_OPTIONS.map((s) => (
                                    <SelectItem key={s.value} value={s.value} data-testid={`progress-status-opt-${s.value.toLowerCase().replace(/\s+/g,'-')}`}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-slate-700">Next milestone</Label>
                        <Input value={milestone}
                               onChange={(e) => setMilestone(e.target.value)}
                               data-testid="progress-milestone-input"
                               className="ring-terra"
                               placeholder="e.g. Kickoff review" />
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between mb-3">
                <p className="overline">Phases</p>
                {editing && (
                    <button onClick={addPhase} data-testid="progress-add-phase-btn"
                            className="text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-2.5 py-1 inline-flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Add phase
                    </button>
                )}
            </div>

            {editing ? (
                <ul className="space-y-2" data-testid="progress-phases-list">
                    {phases.map((ph, i) => (
                        <li key={i} data-testid={`progress-phase-row-${i}`}
                            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-2">
                            <div className="flex flex-col">
                                <button onClick={() => movePhase(i, -1)} disabled={i === 0}
                                        data-testid={`progress-phase-up-${i}`}
                                        className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-50 disabled:opacity-30">
                                    <ArrowUp className="w-3 h-3" />
                                </button>
                                <button onClick={() => movePhase(i, 1)} disabled={i === phases.length - 1}
                                        data-testid={`progress-phase-down-${i}`}
                                        className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-50 disabled:opacity-30">
                                    <ArrowDown className="w-3 h-3" />
                                </button>
                            </div>
                            <Input value={ph.name}
                                   onChange={(e) => updatePhase(i, { name: e.target.value })}
                                   data-testid={`progress-phase-name-${i}`}
                                   className="ring-terra flex-1"
                                   placeholder="Phase name" />
                            <Select value={ph.status} onValueChange={(v) => updatePhase(i, { status: v })}>
                                <SelectTrigger data-testid={`progress-phase-status-${i}`} className="w-40 ring-terra">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PHASE_STATUS.map((s) => (
                                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <button onClick={() => removePhase(i)}
                                    data-testid={`progress-phase-remove-${i}`}
                                    className="p-2 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </li>
                    ))}
                    {phases.length === 0 && (
                        <li className="text-sm text-slate-500 text-center py-6">No phases yet. Add one to show progress steps.</li>
                    )}
                </ul>
            ) : (
                <div className="grid md:grid-cols-4 gap-3">
                    {(portal?.progress?.phases || []).map((ph, i) => {
                        const meta = PHASE_ICON[ph.status] || PHASE_ICON.upcoming;
                        const Icon = meta.icon;
                        return (
                            <div key={`${ph.name}-${i}`} data-testid={`progress-phase-chip-${i}`}
                                 className="rounded-lg border border-slate-200 p-4 flex items-center gap-3">
                                <Icon className={`w-4 h-4 ${meta.cls}`} />
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-slate-900 truncate">{ph.name}</p>
                                    <p className="text-xs text-slate-500 capitalize">{ph.status.replace("_", " ")}</p>
                                </div>
                            </div>
                        );
                    })}
                    {(!portal?.progress?.phases || portal.progress.phases.length === 0) && (
                        <div className="md:col-span-4 text-sm text-slate-500 text-center py-6">No phases defined yet.</div>
                    )}
                </div>
            )}
        </div>
    );
}
